/* eslint-disable react-hooks/exhaustive-deps */
import React,{useEffect,useState} from 'react';
import {useNavigate,useParams} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import {Input,Select,Textarea} from '../components/common/FormField';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';

const unwrap=(r:any)=>r?.data?.data??r?.data??r;

export default function MortuaryAutopsyPage(){
  const {id}=useParams();
  const nav=useNavigate();
  const [pm,setPm]=useState<any>(null);
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState('');
  const [live,setLive]=useState('');
  const [findings,setFindings]=useState<any[]>([]);
  const [sig,setSig]=useState('');
  const [cod,setCod]=useState('');
  const [manner,setManner]=useState('');
  const [sched,setSched]=useState<any>({});
  const [missing,setMissing]=useState(false);

  const load=async()=>{
    try{
      const rec=unwrap(await api.getMortuaryPmByBody(id as string));
      if(!rec||!rec.id){setPm(null);setMissing(true);return;}
      setMissing(false);
      setPm(rec);
      setFindings((rec.findings||[]).length?rec.findings:[{section_code:'GENERAL',section_label:'Examination notes',content:''}]);
      setCod(rec.cause_of_death||'');
      setManner(rec.manner_of_death||'');
      setSched({
        examinationAt:rec.examination_at?String(rec.examination_at).slice(0,16):'',
        examinationLocation:rec.examination_location||'',
        pathologistName:rec.pathologist_name||'',
        assistants:rec.assistants||'',
      });
    }catch(e:any){setMsg(e.response?.data?.message||'Could not open autopsy case');setMissing(true);}
  };
  useEffect(()=>{load();},[id]);

  const run=async(key:string,fn:()=>Promise<any>)=>{
    setSaving(key);
    try{await fn();setMsg('✅ Saved');await load();}
    catch(e:any){setMsg('❌ '+(e.response?.data?.message||'Action failed'));}
    finally{setSaving('');}
  };

  if(!pm) return(
    <Layout>
      <div className="card p-8 max-w-xl mx-auto space-y-3 text-center">
        <h1 className="text-xl font-bold text-[#02357A]">Autopsy workbench</h1>
        <p className="text-sm text-slate-600">{missing?'No PM case yet. Creating a case posts the autopsy charge and starts the forensic workflow.':'Opening autopsy workbench…'}</p>
        {msg&&<p className="text-sm text-red-700">{msg}</p>}
        {missing&&<button disabled={saving==='cr'} className="btn-primary" onClick={()=>run('cr',()=>api.createMortuaryPmCase(id as string,{}))}>Create PM case</button>}
        <button className="text-xs text-[#02357A]" onClick={()=>nav(`/mortuary/bodies/${id}`)}>← Body record</button>
      </div>
    </Layout>
  );
  const locked=!!pm.locked_at;
  const body=pm.body||{};

  return(
    <Layout>
      <div className="space-y-3">
        <div className="flex justify-between gap-3">
          <div>
            <button className="text-xs text-[#02357A]" onClick={()=>nav(`/mortuary/bodies/${body.id||id}`)}>← Daily workbench</button>
            {' · '}
            <button className="text-xs text-[#02357A]" onClick={()=>nav(`/mortuary/bodies/${body.id||id}/case`)}>Full case file</button>
            <h1 className="text-xl font-bold">{pm.pm_number} · Autopsy</h1>
            <p className="text-[11px] text-slate-500">Record findings and sign the report here. Identify / store / release stay on the daily workbench.</p>
            <p className="text-xs text-slate-500">{body.body_number} · {body.full_name} · {pm.status} · {pm.classification}</p>
          </div>
          <Select disabled={locked} value={pm.status} onChange={e=>run('st',()=>api.updateMortuaryPmCase(pm.id,{status:e.target.value}))}>
            {['REQUESTED','SCHEDULED','IN_PROGRESS','COMPLETED','REPORT_PENDING','FINALIZED','ADDENDUM_REQUIRED','CLOSED'].map(s=><option key={s}>{s}</option>)}
          </Select>
        </div>
        {msg&&<div className="text-sm bg-slate-50 border rounded-lg px-3 py-2">{msg}</div>}
        {(pm.quality||[]).map((q:any,i:number)=><p key={i} className="text-xs bg-amber-50 text-amber-800 px-2 py-1 rounded">Potential inconsistency detected. {q.message}</p>)}

        <div className="grid lg:grid-cols-12 gap-3">
          <div className="lg:col-span-3 card p-3 text-xs space-y-1">
            <h3 className="font-semibold text-sm">Case</h3>
            <p>MLC: {pm.mlc?'Yes':'No'}</p>
            <p>Police: {pm.police_case_number||'—'}</p>
            <p>Pathologist: {pm.pathologist_name||'—'}</p>
            <p>Location: {pm.examination_location||'—'}</p>
            <p>Exam: {fmtDateTime(pm.examination_at)}</p>
            <div className="pt-2 space-y-1">
              <p className="font-semibold text-sm">Schedule / assign</p>
              <Input type="datetime-local" disabled={locked} value={sched.examinationAt||''} onChange={e=>setSched({...sched,examinationAt:e.target.value})}/>
              <Input placeholder="Autopsy room / location" disabled={locked} value={sched.examinationLocation||''} onChange={e=>setSched({...sched,examinationLocation:e.target.value})}/>
              <Input placeholder="Pathologist name" disabled={locked} value={sched.pathologistName||''} onChange={e=>setSched({...sched,pathologistName:e.target.value})}/>
              <Input placeholder="Assistants" disabled={locked} value={sched.assistants||''} onChange={e=>setSched({...sched,assistants:e.target.value})}/>
              <button disabled={locked||saving==='sc'} className="btn-primary text-xs w-full" onClick={()=>run('sc',()=>api.updateMortuaryPmCase(pm.id,{
                status:pm.status==='REQUESTED'?'SCHEDULED':pm.status,
                examinationAt:sched.examinationAt||undefined,
                examinationLocation:sched.examinationLocation,
                pathologistName:sched.pathologistName,
                assistants:sched.assistants,
              }))}>Save schedule</button>
            </div>
            <h3 className="font-semibold text-sm pt-2">Timeline</h3>
            {(pm.versions||[]).map((v:any)=><p key={v.id}>{fmtDateTime(v.created_at)} · {v.kind} v{v.version_no}</p>)}
            <h3 className="font-semibold text-sm pt-2">Compliance</h3>
            <Compliance bodyId={body.id||id}/>
          </div>

          <div className="lg:col-span-5 card p-3 space-y-2">
            <h3 className="font-semibold">Live transcription (assistive)</h3>
            <p className="text-[11px] text-slate-500">AI never sets cause or manner of death. Confirm every suggested specimen.</p>
            <Textarea rows={8} value={live} onChange={e=>setLive(e.target.value)} placeholder="Dictate or type examination notes…"/>
            <div className="flex flex-wrap gap-2">
              <button disabled={locked||saving==='tr'} className="btn-primary text-sm" onClick={()=>run('tr',()=>api.transcribeMortuaryPm(pm.id,{text:live}))}>Save transcript</button>
              <button disabled={locked||saving==='ai'} className="btn-secondary text-sm" onClick={()=>run('ai',()=>api.generateMortuaryPmDraft(pm.id))}>Generate draft</button>
            </div>
            <div className="space-y-1">
              {(pm.suggestions||[]).filter((s:any)=>s.status==='PENDING').map((s:any)=>(
                <div key={s.id} className="text-xs bg-indigo-50 border border-indigo-100 rounded p-2">
                  SPECIMEN DETECTED · {s.payload?.specimenType} · {s.payload?.purpose}
                  <button className="ml-2 underline" onClick={()=>run('sg',()=>api.confirmMortuaryAiSuggestion(pm.id,s.id,{bodyId:body.id||id}))}>Create specimen</button>
                </div>
              ))}
            </div>
            <h3 className="font-semibold pt-2">Findings</h3>
            <div className="max-h-[420px] overflow-auto space-y-2">
              {findings.map((f:any,i:number)=>(
                <div key={f.section_code}>
                  <label className="text-[11px] font-semibold text-slate-500">{f.section_label||f.section_code} {f.source==='AI'&&<span className="text-indigo-600">AI draft</span>}</label>
                  <Textarea rows={2} disabled={locked} value={f.content||''} onChange={e=>{
                    const next=[...findings]; next[i]={...f,content:e.target.value}; setFindings(next);
                  }}/>
                </div>
              ))}
            </div>
            <button disabled={locked||saving==='fn'} className="btn-primary text-sm" onClick={()=>run('fn',()=>api.saveMortuaryPmFindings(pm.id,findings))}>Save doctor edits</button>
          </div>

          <div className="lg:col-span-4 space-y-3">
            <div className="card p-3 space-y-2">
              <h3 className="font-semibold">Structured report</h3>
              <Input placeholder="Cause of death (doctor only)" value={cod} onChange={e=>setCod(e.target.value)}/>
              <Select value={manner} onChange={e=>setManner(e.target.value)}>
                <option value="">Manner of death</option>
                {['NATURAL','ACCIDENT','SUICIDE','HOMICIDE','UNDETERMINED'].map(s=><option key={s}>{s}</option>)}
              </Select>
              <Textarea rows={2} placeholder="Type name to sign" value={sig} onChange={e=>setSig(e.target.value)}/>
              <div className="flex flex-wrap gap-2">
                <button disabled={locked||saving==='sg'||!sig.trim()} className="btn-primary text-sm" onClick={()=>{
                  if(!window.confirm('Lock and sign this report? AI drafts are not a legal conclusion.')) return;
                  return run('sg',()=>api.signMortuaryPm(pm.id,{signatureData:sig,causeOfDeath:cod,mannerOfDeath:manner}));
                }}>Sign / finalize</button>
                {locked&&<button className="btn-secondary text-sm" onClick={()=>{
                  const reason=window.prompt('Addendum reason');
                  if(reason) run('ad',()=>api.addendumMortuaryPm(pm.id,reason));
                }}>Open addendum</button>}
              </div>
            </div>
            <div className="card p-3 text-xs space-y-1">
              <h3 className="font-semibold text-sm">Specimens</h3>
              {(pm.specimens||[]).map((s:any)=><p key={s.id}>{s.specimen_number} · {s.specimen_type} · {s.purpose} · {s.status}</p>)}
              <SpecimenQuick bodyId={body.id||id} onDone={load}/>
            </div>
            <div className="card p-3 text-xs space-y-1">
              <h3 className="font-semibold text-sm">Evidence</h3>
              {(pm.evidence||[]).map((e:any)=><p key={e.id}>{e.evidence_number} · {e.category}</p>)}
              <EvidenceQuick bodyId={body.id||id} onDone={load}/>
            </div>
            <div className="card p-3 text-xs space-y-1">
              <h3 className="font-semibold text-sm">Photography / imaging</h3>
              <PhotoQuick bodyId={body.id||id} onDone={load}/>
              <div className="flex gap-1 pt-1">
                {['XR','CT'].map(m=>(
                  <button key={m} disabled={!body.patient_id||saving==='im'} className="btn-secondary text-xs" onClick={()=>run('im',()=>api.orderMortuaryImaging(body.id||id,{modality:m}))}>Order {m}</button>
                ))}
              </div>
              {!body.patient_id&&<p className="text-[11px] text-amber-800">Link a patient on the body record before ordering existing PACS imaging.</p>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Compliance({bodyId}:{bodyId:string}){
  const [items,setItems]=useState<any[]>([]);
  useEffect(()=>{api.getMortuaryCompliance(bodyId).then(r=>setItems(unwrap(r)?.items||[])).catch(()=>null);},[bodyId]);
  return <div>{items.map((i:any)=><p key={i.key} className={i.state==='GREEN'?'text-green-700':i.state==='RED'?'text-red-700':'text-amber-700'}>{i.state} · {i.label}</p>)}</div>;
}

function SpecimenQuick({bodyId,onDone}:{bodyId:string;onDone:()=>void}){
  const [type,setType]=useState('BLOOD');
  const [purpose,setPurpose]=useState('TOXICOLOGY');
  return(
    <div className="flex gap-1 pt-1">
      <Select value={type} onChange={e=>setType(e.target.value)}>{['BLOOD','URINE','VITREOUS','LIVER','KIDNEY','BRAIN','SWAB','DNA'].map(s=><option key={s}>{s}</option>)}</Select>
      <Select value={purpose} onChange={e=>setPurpose(e.target.value)}>{['TOXICOLOGY','HISTOPATHOLOGY','MICROBIOLOGY','DNA','OTHER'].map(s=><option key={s}>{s}</option>)}</Select>
      <button className="btn-secondary text-xs" onClick={async()=>{try{const r=await api.addMortuarySpecimen(bodyId,{specimenType:type,purpose});const d=(r as any)?.data?.data||(r as any)?.data||r; if(d&&!d.lab_request_id) window.alert('Specimen collected. LIS order is created only when the body is linked to a patient.'); onDone();}catch(e:any){window.alert(e.response?.data?.message||'Could not add specimen');}}}>Add</button>
    </div>
  );
}

function EvidenceQuick({bodyId,onDone}:{bodyId:string;onDone:()=>void}){
  const [category,setCategory]=useState('CLOTHING');
  const [description,setDescription]=useState('');
  return(
    <div className="space-y-1 pt-1">
      <Select value={category} onChange={e=>setCategory(e.target.value)}>{['CLOTHING','WEAPON','BIOLOGICAL','DOCUMENT','PHOTOGRAPH','DIGITAL','OTHER'].map(s=><option key={s}>{s}</option>)}</Select>
      <Input placeholder="Description" value={description} onChange={e=>setDescription(e.target.value)}/>
      <button className="btn-secondary text-xs" onClick={async()=>{if(!description)return;await api.addMortuaryEvidence(bodyId,{category,description});setDescription('');onDone();}}>Add evidence</button>
    </div>
  );
}

function PhotoQuick({bodyId,onDone}:{bodyId:string;onDone:()=>void}){
  const [region,setRegion]=useState('HEAD');
  const [description,setDescription]=useState('');
  return(
    <div className="space-y-1">
      <Select value={region} onChange={e=>setRegion(e.target.value)}>{['HEAD','TORSO','LIMBS','EXTERNAL','INTERNAL','OTHER'].map(s=><option key={s}>{s}</option>)}</Select>
      <Input placeholder="Photo description / file ref" value={description} onChange={e=>setDescription(e.target.value)}/>
      <button className="btn-secondary text-xs" onClick={async()=>{await api.addMortuaryPhoto(bodyId,{region,description,fileRef:description});setDescription('');onDone();}}>Record photo</button>
    </div>
  );
}
